import { Box, type SxProps, type Theme } from '@mui/material';
import type { ReactNode } from 'react';
import React from 'react';

import { spaces, typography } from '../../assets/styles/themes/tokens';
import useScreenSize from '../../utils/useScreenSize';

interface BannerBarProps {
	backgroundColor: string;
	color: string;
	children: ReactNode;
	sx?: SxProps<Theme>;
}

export const BannerBar: React.FC<BannerBarProps> = ({ backgroundColor, color, children, sx }) => {
	const { isXsScreen, isSmScreen } = useScreenSize();
	const isMobile = isXsScreen || isSmScreen;

	return (
		<Box
			// Compose as an array so a consumer's overrides merge with the base styles
			// instead of overwriting them.
			sx={[
				{
					backgroundColor,
					color,
					flexDirection: isMobile ? 'column' : 'row',
					textAlign: isMobile ? 'center' : 'left',
					// Reuse the type-scale sizes off the DS `body2`/`body1` variants. We take
					// the sizes (not the `typography` variant shorthand) because that shorthand
					// bakes in the variants' `text.secondary` color — unreadable on the banner.
					fontSize: isMobile ? typography.body2.fontSize : typography.body1.fontSize,
					paddingTop: spaces.xs,
					paddingBottom: spaces.xs,
					paddingRight: spaces.lg,
					paddingLeft: spaces.lg,
					display: 'flex',
				},
				...(Array.isArray(sx) ? sx : [sx]),
			]}
		>
			{children}
		</Box>
	);
};

export const BannerBarMessage: React.FC = ({ children }) => {
	return (
		<Box
			sx={{
				flexGrow: 1,
				minWidth: 0,
				paddingTop: spaces.xs,
				paddingBottom: spaces.xs,
			}}
		>
			{children}
		</Box>
	);
};

export default BannerBar;
